import React from "react";
import { Box, keyframes } from "@mui/material";
import { CasLoader } from "../../brand/CasLoader";

interface TypingIndicatorProps {
  // U.5 — optional terse progress label (e.g. "Creating container…") rendered
  // beside the loader. Same-shape element whose text swaps in place, so
  // provisioning progress never flickers as differently-shaped messages.
  text?: string;
}

// The label shimmers: a highlight sweeps the grey text left to right (U.7; the ARIN Web shimmer of
// RED-568: background-clip text, 1.6 s linear, still under prefers-reduced-motion).
const shimmer = keyframes`
  from { background-position: 100% 0; }
  to { background-position: 0 0; }
`;

// Cas's typing receipt: her perspective ripple beside her portrait (it is
// rendered inside an AgentRow), with no bubble, because it is a receipt, not
// a message (U.8).
export const TypingIndicator: React.FC<TypingIndicatorProps> = ({ text }) => {
  return (
    <Box
      data-testid="typing-indicator"
      sx={{
        display: "flex",
        alignSelf: "flex-start",
        alignItems: "center",
        gap: 0.75,
        py: 0.25,
      }}
    >
      <CasLoader size={24} label="Cas is working" />
      {text && (
        <Box
          component="span"
          data-testid="typing-indicator-text"
          sx={{
            fontSize: 11,
            color: "transparent",
            backgroundImage: (theme) =>
              `linear-gradient(90deg, ${theme.palette.text.disabled} 0%, ${theme.palette.text.disabled} 40%, ${theme.palette.text.secondary} 50%, ${theme.palette.text.disabled} 60%, ${theme.palette.text.disabled} 100%)`,
            backgroundSize: "200% 100%",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            animation: `${shimmer} 1.6s linear infinite`,
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
              backgroundImage: "none",
              color: "text.disabled",
            },
          }}
        >
          {text}
        </Box>
      )}
    </Box>
  );
};
